import { useState } from "react";
import { toast } from "sonner";
import { User, Phone, Mail, MapPin, Check, ArrowRight, CheckCircle2, Loader2, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { getAttribution } from "@/lib/track";
import { Txt, EditImage, ItemTools, AddBtn } from "@/components/public/AboutSections";

export const DEFAULT_SPACE_TYPES = [
  { key: "Coworking", label: "Coworking", desc: "Flexible workspaces", image: "/space-types/coworking.png" },
  { key: "BTS", label: "BTS", desc: "Built-to-suit spaces", image: "/space-types/bts.png" },
  { key: "Managed Office", label: "Managed Office", desc: "Fully managed offices", image: "/space-types/managed-office.png" },
  { key: "Meeting Room", label: "Meeting Room", desc: "Professional meeting spaces", image: "/space-types/meeting-room.png" },
  { key: "Training Room", label: "Training Room", desc: "Learning & training spaces", image: "/space-types/training-room.png" },
];
export const DEFAULT_CITIES = ["Noida", "Greater Noida", "Delhi", "Gurugram", "Ghaziabad", "Faridabad", "Other"];
export const DEFAULT_SEATS = ["4-8", "8-20", "20-40", "40+"];

export default function ContactEnquiry({ content, edit }) {
  const F = (content && content.form) || {};
  const spaceTypes = F.space_types && F.space_types.length ? F.space_types : DEFAULT_SPACE_TYPES;
  const cities = F.cities && F.cities.length ? F.cities : DEFAULT_CITIES;
  const seats = F.seats && F.seats.length ? F.seats : DEFAULT_SEATS;

  const [form, setForm] = useState({ space_type: spaceTypes[0]?.key || "Coworking", name: "", mobile: "", email: "", city: "", seats: "" });
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (!form.space_type) e.space_type = "Select a space type";
    if (!form.name.trim()) e.name = "Full name is required";
    if (!/^[0-9+\-\s()]{7,15}$/.test(form.mobile.trim())) e.mobile = "Enter a valid mobile number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Enter a valid email";
    if (!form.city) e.city = "Select a city";
    if (!form.seats) e.seats = "Select number of seats";
    if (!consent) e.consent = "Please accept the Terms of Use and Privacy Policy";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (edit) return; // editing mode is a preview only
    if (!validate()) { toast.error("Please fix the highlighted fields."); return; }
    setBusy(true);
    try {
      await api.post("/public/leads", {
        name: form.name.trim(), email: form.email.trim(), phone: form.mobile.trim(),
        type: "space_enquiry", space_type: form.space_type, city: form.city, seats: form.seats,
        consent: true, message: `Space enquiry — ${form.space_type} · ${form.seats} seats · ${form.city}`,
        ...getAttribution(),
      });
      setDone(true);
      toast.success("Thank you! Our team will reach out shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-gradient-to-b from-[#eef3ff] to-white border-b border-slate-100" data-testid="contact-enquiry">
      <div className="container-cz py-14 sm:py-16">
        <div className="max-w-3xl mb-10">
          <p className="text-sm font-bold tracking-[0.16em] text-primary mb-3 flex items-center gap-3">FIND YOUR SPACE <span className="w-10 h-px bg-primary/50" /></p>
          <h2 className="font-display text-4xl sm:text-5xl font-extrabold text-slate-900 leading-[1.05]">
            Tell Us What You're <span className="text-primary">Looking For</span>
          </h2>
          <p className="text-lg text-slate-600 mt-4">Whether you need a coworking space, BTS setup, a managed office, meeting room, or training room, we'll help you find the right fit.</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 lg:p-10">
          {done ? (
            <div className="py-16 text-center" data-testid="enquiry-success">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-50 text-emerald-500 flex items-center justify-center mb-5"><CheckCircle2 className="w-8 h-8" /></div>
              <h3 className="font-display text-2xl font-extrabold text-slate-900">Enquiry received!</h3>
              <p className="text-slate-500 mt-2 max-w-sm mx-auto">Thanks {form.name.split(" ")[0]}, our team will get in touch about your {form.space_type} requirement shortly.</p>
              <button onClick={() => { setDone(false); setForm({ space_type: spaceTypes[0]?.key || "Coworking", name: "", mobile: "", email: "", city: "", seats: "" }); setConsent(false); }}
                className="mt-6 text-sm font-semibold text-primary hover:underline" data-testid="enquiry-new">Submit another enquiry</button>
            </div>
          ) : (
            <form onSubmit={submit} noValidate>
              <label className="block text-lg font-bold text-slate-900 mb-4">
                {edit ? <Txt edit={edit} path="form.space_type_label" value={F.space_type_label} placeholder="Select Space Type" /> : (F.space_type_label || "Select Space Type")} <span className="text-primary">*</span>
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5">
                {spaceTypes.map((s, i) => {
                  const id = s.key || s.label;
                  const active = form.space_type === id;
                  const inner = (
                    <>
                      {edit ? (
                        <EditImage edit={edit} path={`form.space_types.${i}.image`} value={s.image || s.img} wrapClass="aspect-[16/10] rounded-xl overflow-hidden mb-3 bg-slate-100" imgClass="w-full h-full object-cover" placeholderLabel="image" />
                      ) : (
                        <div className="aspect-[16/10] rounded-xl overflow-hidden mb-3 bg-slate-100"><img src={s.image || s.img} alt={s.label || s.key} loading="lazy" className="w-full h-full object-cover" /></div>
                      )}
                      <Txt edit={edit} path={`form.space_types.${i}.label`} value={s.label || s.key} as="p" className="font-bold text-base text-slate-900 leading-tight" placeholder="Label" />
                      <Txt edit={edit} path={`form.space_types.${i}.desc`} value={s.desc} as="p" className="text-sm text-slate-500 mt-1 leading-snug" placeholder="Description" />
                    </>
                  );
                  if (edit) {
                    return (
                      <div key={i} className="group/item relative text-center rounded-2xl border-2 border-slate-200 p-4 bg-white">
                        <ItemTools edit={edit} path="form.space_types" index={i} count={spaceTypes.length} />
                        {inner}
                      </div>
                    );
                  }
                  return (
                    <button type="button" key={id} onClick={() => set("space_type", id)} data-testid={`space-${id.toLowerCase().replace(/ /g, "-")}`}
                      className={`group relative text-center rounded-2xl border-2 p-4 transition-all ${active ? "border-primary bg-sky-50/70 shadow-sm" : "border-slate-200 hover:border-slate-300 bg-white"}`}>
                      <span className={`absolute top-3 right-3 z-10 w-6 h-6 rounded-full flex items-center justify-center border-2 transition-colors ${active ? "bg-primary border-primary text-white" : "bg-white border-slate-300"}`}>
                        {active && <Check className="w-3.5 h-3.5" />}
                      </span>
                      {inner}
                    </button>
                  );
                })}
              </div>
              {edit && <div className="mt-4"><AddBtn edit={edit} path="form.space_types" blank={{ key: "New Type", label: "New Type", desc: "Description", image: "" }} label="Add space type" /></div>}
              {errors.space_type && <p className="text-xs text-rose-500 mt-2">{errors.space_type}</p>}

              <div className="grid md:grid-cols-2 gap-6 mt-8">
                <Field labelNode={edit ? <Txt edit={edit} path="form.name_label" value={F.name_label} placeholder="Full Name" /> : (F.name_label || "Full Name")} error={errors.name}>
                  <InputIcon icon={User}><input data-testid="enquiry-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={F.name_placeholder || "Type your full name here"} className="input-cz" /></InputIcon>
                </Field>
                <Field labelNode={edit ? <Txt edit={edit} path="form.mobile_label" value={F.mobile_label} placeholder="Mobile" /> : (F.mobile_label || "Mobile")} error={errors.mobile}>
                  <InputIcon icon={Phone}><input data-testid="enquiry-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder={F.mobile_placeholder || "Type your mobile number here"} className="input-cz" inputMode="tel" /></InputIcon>
                </Field>
              </div>

              <div className="mt-6">
                <Field labelNode={edit ? <Txt edit={edit} path="form.email_label" value={F.email_label} placeholder="Email" /> : (F.email_label || "Email")} error={errors.email}>
                  <InputIcon icon={Mail}><input data-testid="enquiry-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder={F.email_placeholder || "Type your email here"} className="input-cz" type="email" /></InputIcon>
                </Field>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mt-6">
                <Field labelNode={edit ? <Txt edit={edit} path="form.city_label" value={F.city_label} placeholder="City" /> : (F.city_label || "City")} error={errors.city}>
                  {edit ? (
                    <div className="space-y-2" data-testid="edit-cities">
                      {cities.map((c, i) => (
                        <div key={i} className="group/item relative flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
                          <ItemTools edit={edit} path="form.cities" index={i} count={cities.length} vertical />
                          <Txt edit={edit} path={`form.cities.${i}`} value={c} className="flex-1" placeholder="City name" />
                        </div>
                      ))}
                      <AddBtn edit={edit} path="form.cities" blank={"New City"} label="Add city" />
                    </div>
                  ) : (
                    <div className="relative">
                      <MapPin className="w-[18px] h-[18px] text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <select data-testid="enquiry-city" value={form.city} onChange={(e) => set("city", e.target.value)}
                        className={`input-cz !pl-11 !pr-9 appearance-none cursor-pointer ${form.city ? "text-slate-900" : "text-slate-400"}`}>
                        <option value="" disabled>{F.city_placeholder || "Select City"}</option>
                        {cities.map((c) => <option key={c} value={c} className="text-slate-900">{c}</option>)}
                      </select>
                      <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  )}
                </Field>
                <Field labelNode={edit ? <Txt edit={edit} path="form.seats_label" value={F.seats_label} placeholder="Number of seats" /> : (F.seats_label || "Number of seats")} error={errors.seats}>
                  {edit ? (
                    <div className="space-y-2" data-testid="edit-seats">
                      {seats.map((s, i) => (
                        <div key={i} className="group/item relative flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
                          <ItemTools edit={edit} path="form.seats" index={i} count={seats.length} vertical />
                          <Txt edit={edit} path={`form.seats.${i}`} value={s} className="flex-1" placeholder="Option" />
                        </div>
                      ))}
                      <AddBtn edit={edit} path="form.seats" blank={"New"} label="Add option" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 gap-2.5" data-testid="enquiry-seats">
                      {seats.map((s) => (
                        <button type="button" key={s} onClick={() => set("seats", s)} data-testid={`seats-${s}`}
                          className={`h-12 rounded-lg text-sm font-medium border transition-all ${form.seats === s ? "bg-primary text-white border-primary" : "bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-300"}`}>{s}</button>
                      ))}
                    </div>
                  )}
                </Field>
              </div>

              <label className="flex items-start gap-3 mt-7 cursor-pointer">
                <input type="checkbox" data-testid="enquiry-consent" checked={consent} onChange={(e) => { setConsent(e.target.checked); setErrors((er) => ({ ...er, consent: undefined })); }}
                  className="mt-1 w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary shrink-0" />
                <span className="text-sm text-slate-500 leading-relaxed">
                  {edit
                    ? <Txt edit={edit} path="form.consent" value={F.consent} placeholder="Consent text" />
                    : (F.consent ? F.consent : <>By submitting this form, you acknowledge our <Link to="/terms-of-use" className="text-primary font-medium hover:underline">Terms of Use</Link> and <Link to="/privacy-policy" className="text-primary font-medium hover:underline">Privacy Policy</Link> and agree to receive marketing emails and promotional offers from CoWorkZen.</>)}
                </span>
              </label>
              {errors.consent && <p className="text-xs text-rose-500 mt-1.5">{errors.consent}</p>}

              <button type="submit" disabled={busy} data-testid="enquiry-submit"
                className="w-full mt-7 rounded-xl bg-primary text-white font-semibold text-base flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors disabled:opacity-70 py-4">
                {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</> : <>{edit ? <Txt edit={edit} path="form.submit_label" value={F.submit_label} placeholder="Submit" /> : (F.submit_label || "Submit")} <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ labelNode, error, children }) {
  return (
    <div>
      <label className="block text-[15px] font-bold text-slate-900 mb-2">{labelNode} <span className="text-primary">*</span></label>
      {children}
      {error && <p className="text-xs text-rose-500 mt-1.5">{error}</p>}
    </div>
  );
}

function InputIcon({ icon: Icon, children }) {
  return (
    <div className="relative">
      <Icon className="w-[18px] h-[18px] text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10" />
      <div className="[&>input]:pl-10">{children}</div>
    </div>
  );
}
